/**
 * Les profils d'architecte de l'espace de travail.
 *
 * Un profil regroupe les affaires d'un même bureau ; ouvrir un profil montre le
 * glossaire consolidé de toutes ses affaires — le vocabulaire que cet architecte
 * réemploie d'un projet à l'autre. La liste vit au niveau de l'espace, hors de
 * toute affaire, d'où sa place dans le rail « Espace de travail ».
 */
import { useState } from "react";

import { api, ErreurApi } from "../api/client";
import type { Profil } from "../api/types";
import { Avis } from "../composants/avis";
import { Chargement, Echec, Vide } from "../composants/etats";
import { useDonnees } from "../composants/donnees";
import { naviguer } from "../routeur";

export function Profils() {
  const liste = useDonnees<{ donnees: Profil[] }>(
    () => api.lire<{ donnees: Profil[] }>("/api/v1/profils"),
    [],
  );
  const [creation, poserCreation] = useState(false);
  const profils = liste.donnees?.donnees ?? [];

  return (
    <div className="ecran">
      <div className="ecran__tete">
        <div className="rangee rangee--espacee rangee--bas">
          <div>
            <h1 className="ecran__titre">Profils</h1>
            <div className="ecran__compte">
              Les bureaux d'architecte de l'espace — chacun consolide le glossaire
              de ses affaires.
            </div>
          </div>
          <button
            type="button"
            className="bouton bouton--primaire"
            onClick={() => poserCreation((c) => !c)}
          >
            Nouveau profil
          </button>
        </div>
      </div>

      <div className="ecran__corps pile">
        {creation && (
          <FormulaireProfil
            surCree={() => { poserCreation(false); liste.recharger(); }}
            surAnnule={() => poserCreation(false)}
          />
        )}

        {liste.charge && !liste.donnees ? (
          <Chargement lignes={4} />
        ) : liste.erreur ? (
          <Echec erreur={liste.erreur} reessayer={liste.recharger} />
        ) : profils.length === 0 && !creation ? (
          <div className="carte">
            <Vide
              titre="Aucun profil"
              aide="Créez un profil d'architecte, puis affectez-lui des affaires depuis leur onglet Glossaire."
              action={
                <button type="button" className="bouton bouton--primaire"
                        onClick={() => poserCreation(true)}>
                  Nouveau profil
                </button>
              }
            />
          </div>
        ) : (
          <div className="grille grille--2">
            {profils.map((p) => (
              <button
                key={p.profil_id}
                type="button"
                className="carte profil-carte"
                onClick={() => naviguer(`/profils/${p.profil_id}`)}
              >
                <div className="carte__corps pile pile--serree">
                  <h3 style={{ margin: 0 }}>{p.nom}</h3>
                  {p.cabinet && <span className="discret">{p.cabinet}</span>}
                  <div className="profil-carte__pied discret">
                    <b>{p.affaires ?? 0}</b> affaire{(p.affaires ?? 0) > 1 ? "s" : ""}
                    {p.contact ? ` · ${p.contact}` : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Le formulaire de création d'un profil — replié tant qu'on ne le demande pas. */
function FormulaireProfil({
  surCree, surAnnule,
}: {
  surCree: () => void;
  surAnnule: () => void;
}) {
  const [nom, poserNom] = useState("");
  const [cabinet, poserCabinet] = useState("");
  const [contact, poserContact] = useState("");
  const [courriel, poserCourriel] = useState("");
  const [telephone, poserTelephone] = useState("");
  const [envoi, poserEnvoi] = useState(false);
  const [erreur, poserErreur] = useState("");

  async function creer() {
    poserEnvoi(true);
    poserErreur("");
    try {
      await api.ecrire("/api/v1/profils", {
        nom, cabinet, contact, courriel, telephone,
      });
      surCree();
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi ? exc.message
          : "Le profil n'a pas pu être créé.",
      );
    } finally {
      poserEnvoi(false);
    }
  }

  return (
    <div className="carte">
      <div className="carte__corps pile">
        <h3 style={{ margin: 0 }}>Nouveau profil</h3>
        {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}
        <div className="grille grille--2">
          <div className="champ">
            <label htmlFor="p-nom">Nom *</label>
            <input id="p-nom" value={nom} maxLength={120}
                   onChange={(e) => poserNom(e.target.value)}
                   placeholder="Cabinet Durand" />
          </div>
          <div className="champ">
            <label htmlFor="p-cab">Cabinet / agence</label>
            <input id="p-cab" value={cabinet} maxLength={200}
                   onChange={(e) => poserCabinet(e.target.value)}
                   placeholder="Durand & Associés, Nanterre" />
          </div>
          <div className="champ">
            <label htmlFor="p-con">Contact</label>
            <input id="p-con" value={contact} maxLength={120}
                   onChange={(e) => poserContact(e.target.value)}
                   placeholder="Marie Durand" />
          </div>
          <div className="champ">
            <label htmlFor="p-cou">Courriel</label>
            <input id="p-cou" type="email" value={courriel} maxLength={200}
                   onChange={(e) => poserCourriel(e.target.value)}
                   placeholder="contact@durand-archi.fr" />
          </div>
          <div className="champ">
            <label htmlFor="p-tel">Téléphone</label>
            <input id="p-tel" value={telephone} maxLength={60}
                   onChange={(e) => poserTelephone(e.target.value)}
                   placeholder="01 23 45 67 89" />
          </div>
        </div>
        <div className="rangee" style={{ gap: "var(--mo-space-2)" }}>
          <button type="button" className="bouton bouton--primaire"
                  disabled={envoi || !nom.trim()} onClick={creer}>
            {envoi ? "Création…" : "Créer le profil"}
          </button>
          <button type="button" className="bouton bouton--discret"
                  onClick={surAnnule}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
